/* eslint-disable */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, History, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));
const TYPE_LABEL = { sale: "Vente", manual_adjust: "Ajustement manuel", restore: "Restore", recalc: "Recalcul" };
const typeColor = (t) => t === "sale" ? "text-rose-300" : t === "manual_adjust" ? "text-amber-300" : t === "recalc" ? "text-cyan-300" : "text-fuchsia-300";

export const ChangesTable = ({ changes }) => (
  <table className="w-full text-xs mb-3" data-testid="ai-changes-table">
    <thead><tr className="text-amber-400 uppercase tracking-widest text-[10px]"><th className="text-left py-1">Champ</th><th className="text-left">Avant</th><th className="text-left">Après</th></tr></thead>
    <tbody>
      {changes.map((c, i) => (
        <tr key={i} className="border-t border-amber-500/20" data-testid={`ai-change-row-${c.field}`}>
          <td className="py-1 text-amber-100 font-bold">{c.field}</td>
          <td className="text-zinc-300 line-through decoration-rose-400/60">{fmt(c.before)}</td>
          <td className="text-emerald-300 font-black">{fmt(c.after)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ManualAdjust = ({ userId, onPreview }) => {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!delta || !reason.trim()) return toast.error("Delta et motif obligatoires");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ceo/ai-assistant/preview`, { tool: "adjust_stock_manual", args: { user_id: userId, delta, reason: reason.trim() } });
      onPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 bg-zinc-950/70 border border-amber-500/40 rounded-xl p-3" data-testid="manual-adjust-form">
      <p className="text-amber-300 text-[10px] font-black uppercase tracking-widest mb-2">Réglage manuel du stock</p>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={() => setDelta((d) => d - 1)} className="w-8 h-8 rounded-lg bg-rose-600 hover:bg-rose-500 flex items-center justify-center" data-testid="manual-adjust-minus"><Minus className="w-4 h-4 text-white" /></button>
        <input type="number" value={delta} onChange={(e) => setDelta(parseInt(e.target.value || "0", 10))} className="w-20 text-center bg-zinc-900 border border-zinc-700 rounded-lg py-1.5 text-white font-black" data-testid="manual-adjust-delta" />
        <button onClick={() => setDelta((d) => d + 1)} className="w-8 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center justify-center" data-testid="manual-adjust-plus"><Plus className="w-4 h-4 text-white" /></button>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (obligatoire)" className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-xs outline-none focus:border-amber-500" data-testid="manual-adjust-reason" />
      </div>
      <button onClick={submit} disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-xs py-2 rounded-lg" data-testid="manual-adjust-preview">
        {busy ? "…" : "Prévisualiser l'ajustement"}
      </button>
    </div>
  );
};

export const MovementsCard = ({ data, onPreview }) => {
  const u = data.user;
  const drift = u.total_sold !== u.expected_sold;
  return (
    <div className={`rounded-xl p-3 border-2 ${u.is_negative ? "border-rose-500 bg-rose-950/40" : "border-cyan-500/40 bg-zinc-900/80"}`} data-testid={`movements-card-${u.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <History className="w-4 h-4 text-cyan-300" />
        <p className="text-white font-black text-sm">{u.name} <span className="text-zinc-400 text-[10px] uppercase">{u.role} · {u.country}</span></p>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2 text-center">
        {[["Initial", u.initial_stock], ["Actuel", u.current_stock], ["Vendus", u.total_sold], ["Attendu", u.expected_sold]].map(([k, v]) => (
          <div key={k} className="bg-black/40 rounded-lg py-1.5"><p className="text-zinc-400 text-[9px] uppercase">{k}</p><p className={`font-black text-sm ${k === "Actuel" && u.is_negative ? "text-rose-400" : "text-white"}`} data-testid={`mv-stat-${k}`}>{v}</p></div>
        ))}
      </div>
      {(u.is_negative || drift) && (
        <p className="text-rose-300 text-xs mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{u.is_negative ? "Stock négatif — à corriger manuellement. " : ""}{drift ? `Vendus (${u.total_sold}) ≠ initial − actuel (${u.expected_sold}).` : ""}</p>
      )}
      <div className="max-h-48 overflow-y-auto space-y-1 pr-1" data-testid="movements-list">
        {data.movements.length === 0 && <p className="text-zinc-400 text-xs">Aucun mouvement enregistré.</p>}
        {data.movements.map((m, i) => (
          <div key={i} className="flex items-start gap-2 text-[11px] bg-black/30 rounded-md px-2 py-1">
            <span className="text-zinc-400 w-20 flex-shrink-0">{String(m.date).slice(0, 10)}</span>
            <span className={`w-28 flex-shrink-0 font-bold ${typeColor(m.type)}`}>{TYPE_LABEL[m.type] || m.type}</span>
            {m.delta !== null && m.delta !== undefined && <span className={`w-8 font-black ${m.delta < 0 ? "text-rose-400" : "text-emerald-400"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</span>}
            <span className="text-zinc-200 flex-1 break-words">{m.detail}</span>
          </div>
        ))}
      </div>
      <ManualAdjust userId={u.id} onPreview={onPreview} />
    </div>
  );
};

export const AnomaliesCard = ({ data, onOpenUser }) => (
  <div className="rounded-xl p-3 border-2 border-rose-500/50 bg-zinc-900/80" data-testid="anomalies-card">
    <p className="text-rose-300 font-black text-xs uppercase tracking-widest mb-2">{data.count} anomalie(s) détectée(s)</p>
    <div className="space-y-1 max-h-56 overflow-y-auto">
      {data.anomalies.map((a) => (
        <div key={a.user_id} className="bg-black/30 rounded-md px-2 py-1.5 flex items-center gap-2" data-testid={`anomaly-${a.user_id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-bold">{a.name}</p>
            <p className="text-zinc-300 text-[11px]">{a.issues.join(" · ")}</p>
          </div>
          <button onClick={() => onOpenUser(a.user_id)} className="text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-2 py-1 rounded" data-testid={`anomaly-open-${a.user_id}`}>Mouvements</button>
        </div>
      ))}
    </div>
  </div>
);

export const AuditLog = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(`${API}/ceo/ai-assistant/audit-log`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  if (!items) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-400" /></div>;
  return (
    <div className="space-y-2" data-testid="ai-audit-log">
      {items.length === 0 && <p className="text-zinc-400 text-sm text-center py-8">Aucune action enregistrée.</p>}
      {items.map((it) => (
        <div key={it.id} className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-3" data-testid={`audit-item-${it.id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-fuchsia-300 text-xs font-black">{it.tool}</span>
            <span className="text-zinc-400 text-[10px]">{new Date(it.created_at).toLocaleString()}</span>
          </div>
          <p className="text-white text-xs">{it.result?.message}</p>
          {it.args?.reason && <p className="text-amber-300 text-[11px] mt-1">Motif: {it.args.reason}</p>}
          {(it.changes || []).length > 0 && <div className="mt-2"><ChangesTable changes={it.changes} /></div>}
        </div>
      ))}
    </div>
  );
};
